// Central garment-type registry. This is configuration, not retailer data —
// it says which measurements matter for a garment type and how they're
// weighted, the same way ComparisonSettings already configured chest/waist
// weights for the generic tops/bottoms categories. No brand measurement
// numbers are invented here.
//
// Coverage note: the brief specified explicit measurement priorities for
// T-Shirts, Golf Polos, Knitwear, Quarter-Zips, Puffers, Jeans, Track Pants,
// Chinos, Boardshorts, and Running Shorts. Pull-Overs, Cardigans, Hoodies,
// Performance T-Shirts, Performance Golf Polos, Cotton Trousers, Baggy
// Pants, and Cotton Shorts weren't given explicit lists — each is
// configured here to match its closest specified sibling's pattern
// (documented per entry below), not invented from nothing.
//
// Only measurements with a real SizeRow column are weighted numerically:
// chest, waist, hip, shoulder, sleeve, thigh, inseam. Qualitative
// attributes mentioned in the brief (body length, rise, outseam, layering
// allowance) don't have dedicated numeric columns yet — see
// SizeChart.cutNotes for where that free-text context is captured instead.

import type { BottomGarmentType, GarmentGroup, GarmentType, MeasurementKey, TopGarmentType } from '@/types/domain';

export interface GarmentTypeConfig {
  slug: GarmentType;
  name: string;
  group: GarmentGroup;
  /** Measurements worth showing/considering for this type, in priority order. */
  relevantMeasurements: MeasurementKey[];
  /** Scoring weights for the subset of relevantMeasurements we actually weight. Sums to 1. */
  weights: Partial<Record<MeasurementKey, number>>;
  /** Short, honest description of fit behaviour for this garment type — not a fabricated claim about any specific brand. */
  notes: string;
}

const TOP_TYPES: Record<TopGarmentType, GarmentTypeConfig> = {
  't-shirts': {
    slug: 't-shirts',
    name: 'T-Shirts',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'waist', 'sleeve'],
    weights: { chest: 0.55, waist: 0.25, shoulder: 0.2 },
    notes: 'Sleeve length is shown when published but not scored — it rarely determines overall fit for a T-shirt.',
  },
  'golf-polos': {
    slug: 'golf-polos',
    name: 'Golf Polos',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'waist', 'sleeve'],
    weights: { chest: 0.5, waist: 0.25, shoulder: 0.25 },
    notes: 'Body length is often published but has no dedicated column yet — see chart notes where available.',
  },
  knitwear: {
    slug: 'knitwear',
    name: 'Knitwear',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'sleeve'],
    weights: { chest: 0.6, shoulder: 0.4 },
    notes: 'Waist is usually not a distinguishing measurement for knitwear, so it is not weighted here.',
  },
  'pull-overs': {
    slug: 'pull-overs',
    name: 'Pull-Overs',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'sleeve', 'waist'],
    weights: { chest: 0.55, shoulder: 0.25, waist: 0.2 },
    notes: 'Not given an explicit priority list in the brief — configured to match Knitwear/Hoodies.',
  },
  cardigans: {
    slug: 'cardigans',
    name: 'Cardigans',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'sleeve', 'waist'],
    weights: { chest: 0.55, shoulder: 0.25, waist: 0.2 },
    notes: 'Not given an explicit priority list in the brief — configured to match Knitwear.',
  },
  'quarter-zips': {
    slug: 'quarter-zips',
    name: 'Quarter-Zips',
    group: 'tops',
    relevantMeasurements: ['chest', 'waist', 'shoulder', 'sleeve'],
    weights: { chest: 0.5, waist: 0.3, shoulder: 0.2 },
    notes: 'Body length is often published but has no dedicated column yet.',
  },
  hoodies: {
    slug: 'hoodies',
    name: 'Hoodies',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'sleeve', 'waist'],
    weights: { chest: 0.5, shoulder: 0.25, waist: 0.25 },
    notes: 'Not given an explicit priority list in the brief — configured to match Quarter-Zips/Knitwear.',
  },
  puffers: {
    slug: 'puffers',
    name: 'Puffers',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'waist'],
    weights: { chest: 0.5, shoulder: 0.25, waist: 0.25 },
    notes: 'Layering allowance and garment cut are qualitative — see chart-level cutNotes/garmentCut, not scored numerically.',
  },
  'performance-t-shirts': {
    slug: 'performance-t-shirts',
    name: 'Performance T-Shirts',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'waist', 'sleeve'],
    weights: { chest: 0.55, waist: 0.25, shoulder: 0.2 },
    notes: 'Same priorities as T-Shirts; stretch profile is captured separately as a secondary modifier.',
  },
  'performance-golf-polos': {
    slug: 'performance-golf-polos',
    name: 'Performance Golf Polos',
    group: 'tops',
    relevantMeasurements: ['chest', 'shoulder', 'waist', 'sleeve'],
    weights: { chest: 0.5, waist: 0.25, shoulder: 0.25 },
    notes: 'Same priorities as Golf Polos; stretch profile is captured separately as a secondary modifier.',
  },
};

const BOTTOM_TYPES: Record<BottomGarmentType, GarmentTypeConfig> = {
  jeans: {
    slug: 'jeans',
    name: 'Jeans',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'thigh', 'inseam'],
    weights: { waist: 0.4, hip: 0.3, thigh: 0.15, inseam: 0.15 },
    notes: 'Rise is often published but has no dedicated column yet.',
  },
  'track-pants': {
    slug: 'track-pants',
    name: 'Track Pants',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'thigh', 'inseam'],
    weights: { waist: 0.4, hip: 0.3, thigh: 0.15, inseam: 0.15 },
    notes: 'Stretch profile is captured separately as a secondary modifier.',
  },
  chinos: {
    slug: 'chinos',
    name: 'Chinos',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'thigh', 'inseam'],
    weights: { waist: 0.4, hip: 0.3, thigh: 0.15, inseam: 0.15 },
    notes: 'Rise is often published but has no dedicated column yet.',
  },
  'cotton-trousers': {
    slug: 'cotton-trousers',
    name: 'Cotton Trousers',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'thigh', 'inseam'],
    weights: { waist: 0.4, hip: 0.3, thigh: 0.15, inseam: 0.15 },
    notes: 'Not given an explicit priority list in the brief — configured to match Chinos.',
  },
  'baggy-pants': {
    slug: 'baggy-pants',
    name: 'Baggy Pants',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'thigh', 'inseam'],
    weights: { waist: 0.4, hip: 0.3, thigh: 0.15, inseam: 0.15 },
    notes: 'Not given an explicit priority list in the brief — configured to match Track Pants; intended cut is baggy/relaxed via garmentCut.',
  },
  boardshorts: {
    slug: 'boardshorts',
    name: 'Boardshorts',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip'],
    weights: { waist: 0.55, hip: 0.45 },
    notes: 'Outseam is often published but has no dedicated column yet. Stretch profile is a secondary modifier.',
  },
  'cotton-shorts': {
    slug: 'cotton-shorts',
    name: 'Cotton Shorts',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip'],
    weights: { waist: 0.55, hip: 0.45 },
    notes: 'Not given an explicit priority list in the brief — configured to match Boardshorts.',
  },
  'running-shorts': {
    slug: 'running-shorts',
    name: 'Running Shorts',
    group: 'bottoms',
    relevantMeasurements: ['waist', 'hip', 'inseam'],
    weights: { waist: 0.45, hip: 0.35, inseam: 0.2 },
    notes: 'Stretch profile and compression-vs-relaxed cut are secondary modifiers, not scored numerically.',
  },
};

export const GARMENT_TYPE_REGISTRY: Record<GarmentType, GarmentTypeConfig> = {
  ...TOP_TYPES,
  ...BOTTOM_TYPES,
};

export const TOP_GARMENT_TYPES: GarmentTypeConfig[] = Object.values(TOP_TYPES);
export const BOTTOM_GARMENT_TYPES: GarmentTypeConfig[] = Object.values(BOTTOM_TYPES);

export function garmentTypesForGroup(group: GarmentGroup): GarmentTypeConfig[] {
  return group === 'tops' ? TOP_GARMENT_TYPES : BOTTOM_GARMENT_TYPES;
}
